import { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useProjectMembership } from '../../hooks/useProjectMembership';
import { Privilege } from '../../lib/permissions';

interface PermissionTooltipProps {
  /** Privilege required to enable the wrapped action. */
  privilege: Privilege;
  /** The button (or any element accepting `disabled` / `aria-disabled`). */
  children: ReactNode;
  /**
   * Optional override of the projectId. Defaults to `projectId` from the
   * router params (works on every page nested under `/project/:projectId/`).
   */
  projectId?: string;
  /** Optional override for the disabled-state tooltip text. */
  disabledTooltip?: string;
}

const PRIVILEGE_LABELS: Record<Privilege, string> = {
  generate: 'generate documents',
  upload: 'upload files',
  download: 'download or export documents',
  edit: 'edit content',
  approve: 'approve artifacts',
  comment: 'comment',
  manage_members: 'manage members',
  view_activity: 'view the activity log',
  issue_documents: 'approve and issue document versions',
};

/**
 * Wraps a button (or anything with pointer events) and disables it when
 * the current user's role doesn't grant the privilege.
 *
 * Use the simpler `usePermission(privilege)` boolean hook directly when
 * the disable logic is intertwined with other state.
 */
export function PermissionTooltip({
  privilege,
  children,
  projectId: explicitProjectId,
  disabledTooltip,
}: PermissionTooltipProps) {
  const params = useParams<{ projectId: string }>();
  const projectId = explicitProjectId || params.projectId || '';
  const { has, loading } = useProjectMembership(projectId);
  const allowed = loading || has(privilege);

  if (allowed) return <>{children}</>;

  const tooltip =
    disabledTooltip ||
    `Your role doesn't allow you to ${PRIVILEGE_LABELS[privilege]} on this project.`;
  return (
    <span
      title={tooltip}
      className="inline-block opacity-50 pointer-events-none cursor-not-allowed"
      aria-disabled
    >
      {children}
    </span>
  );
}

/**
 * Boolean hook for inline use:
 *   const canGenerate = usePermission('generate');
 *   <button disabled={!canGenerate}>Generate</button>
 */
export function usePermission(
  privilege: Privilege,
  explicitProjectId?: string
): boolean {
  const params = useParams<{ projectId: string }>();
  const projectId = explicitProjectId || params.projectId || '';
  const { has, loading } = useProjectMembership(projectId);
  // Optimistic during load so buttons aren't briefly disabled on each
  // page navigation.
  return loading || has(privilege);
}
